/**
 * API Error Handler
 *
 * Centralized error handling for API calls.
 * Provides consistent logging and error formatting across the application.
 */

import { reportError } from './error-reporter';

/**
 * Handles API errors with consistent logging
 *
 * @param operation - Description of the operation that failed
 * @param error - The error object
 *
 * @example
 * ```ts
 * void updatePresence(code, count).catch((error) =>
 *   handleApiError('update presence', error)
 * );
 * ```
 */
export function handleApiError(operation: string, error: unknown): void {
  const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

  console.error(`Failed to ${operation}:`, {
    operation,
    error: errorMessage,
    timestamp: new Date().toISOString()
  });

  reportError(error, `api-error:${operation}`);
}

/**
 * Handles API errors with custom error callback
 *
 * @param operation - Description of the operation that failed
 * @param error - The error object
 * @param onError - Custom error handler callback
 *
 * @example
 * ```ts
 * void startGame(code).catch((error) =>
 *   handleApiErrorWithCallback('start game', error, setRoomError)
 * );
 * ```
 */
export function handleApiErrorWithCallback(
  operation: string,
  error: unknown,
  onError: (message: string) => void
): void {
  handleApiError(operation, error);

  const errorMessage = error instanceof Error ? error.message : `Failed to ${operation}`;

  onError(errorMessage);
}
